import React, { useEffect, useState } from 'react';
import { View, ScrollView, StyleProp, ViewStyle } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import Selectors from '@styles/selectors';
import ColorWrapper from '@styles/colors';
import ProductionInformation from '@components/productionInformation';

import { getLinkingModalConfig, isWeb, Popup } from '@helpers';
import { fullWidth, scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { OperationSearchItem } from '@interfaces/search';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ColorFunction } from '@styles/colors/types';
import { Machine } from '@interfaces/machines';
import { BOTTOM_SHEET_TOP_OFFSET } from '@v2/pages/common/constants/base.constants';
import { useRoute } from '@react-navigation/native';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';

interface JobsListSelectorProps {
  jobs: OperationSearchItem[];
  font?: typeof fontOP;
  selectedJob?: number;
  setSelectedJob: React.Dispatch<React.SetStateAction<number | undefined>>;
  scale?: ScaleFunction;
  C: ColorFunction;
  selectedItem?: Machine;
  style?: StyleProp<ViewStyle>;
}

const JobsListSelector: React.FC<JobsListSelectorProps> = (props) => {
  const { jobs, scale = scaleOP, font = fontOP, selectedJob, setSelectedJob, C, selectedItem, style } = props;
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const [scrollViewWidth, setScrollViewWidth] = useState<number>(0);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const route = useRoute();
  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];
  const isMachinePerspective = selectedJobId == null;

  useEffect(() => {
    setIsOverflowing(contentWidth > scrollViewWidth);
  }, [contentWidth, scrollViewWidth]);

  const onAddJobPress = () => {
    Popup({
      Component: (p: any) => (
        <ProductionInformation
          {...{
            ...p,
            scale,
            font,
            selectedItem,
            productionBatchId: selectedItem?.machineStatus?.productionBatchId,
            selectedNCFileId: selectedItem?.machineStatus?.ncFileId,
            partId: selectedItem?.machineStatus?.partDetails?.partId,
            isOperatorPanel: true,
            title: 'Link Batch to Job',
          }}
        />
      ),
      ...getLinkingModalConfig(scale),
      offsetTop: isWeb ? scale(18) : scale(BOTTOM_SHEET_TOP_OFFSET / 2),
      offsetBottom: scale(18),
      width: (isWeb ? fullWidth : width) - scale((isWeb ? fullWidth : width) > 1116 ? 36 : 0),
    });
  };

  return (
    <View
      style={[
        {
          display: 'flex',
          flexDirection: 'row',
          gap: scale(9),
        },
        style,
      ]}
    >
      <ScrollView
        style={{ display: 'flex', flexDirection: 'row', alignSelf: 'auto', flex: 1 }}
        horizontal
        contentContainerStyle={{ gap: scale(9) }}
        showsHorizontalScrollIndicator={false}
        onLayout={(event) => {
          const { width } = event.nativeEvent.layout;
          setScrollViewWidth(width);
        }}
        onContentSizeChange={(contentWidth) => {
          setContentWidth(contentWidth);
        }}
      >
        {jobs.map((job) => {
          const isActive = job.productionOperationId === selectedJob;

          return (
            <View
              key={job.productionOperationId}
              style={{
                borderWidth: scale(1),
                borderColor: isActive ? 'transparent' : C('Label/Light/Tertiary'),
                borderRadius: scale(18),
              }}
            >
              <Selectors
                scale={scale}
                font={font}
                selected={isActive}
                variant="M"
                text={job.productionOperationNumber}
                onPress={() => setSelectedJob(job.productionOperationId)}
              />
            </View>
          );
        })}
        {isMachinePerspective && !isOverflowing && (
          <View
            style={{
              borderRadius: scale(18),
              borderWidth: scale(1),
              borderStyle: 'dashed',
              borderColor: C('Label/Light/Tertiary'),
            }}
          >
            <Selectors scale={scale} font={font} selected={false} variant="M" text={i18n.t('Add Job')} onPress={onAddJobPress} />
          </View>
        )}
      </ScrollView>
      {isOverflowing && isMachinePerspective && (
        <View
          style={{ alignSelf: 'auto', borderRadius: scale(18), borderWidth: scale(1), borderStyle: 'dashed', borderColor: C('Label/Light/Tertiary') }}
        >
          <Selectors scale={scale} font={font} selected={false} variant="M" text={i18n.t('Add Job')} onPress={onAddJobPress} />
        </View>
      )}
    </View>
  );
};

export default connect(({ machines: { selectedItem } }) => ({ selectedItem }))(ColorWrapper(JobsListSelector));
